import { OccupationalBaselines } from '../../../components/OccupationalBaselines';
import { useEffect, useRef, useState } from 'react';
import { EXAM_TYPE_LABEL } from '@dru/types';
import { Link, useNavigate } from 'react-router-dom';
import type { PhysicianRow, ReviewQueueRow } from '@dru/types';
import { Alert, Button, Card, DataTable, SearchInput, StatusBadge, cn, MoreActions } from '@dru/ui';
import { Bell, ChevronDown, ClipboardCheck, Clock, FilePenLine, Inbox, PauseCircle, RefreshCw, RotateCcw, Siren } from 'lucide-react';
import { api } from '../../../lib/api';
import { formatDate, formatDateTime } from '../../../lib/format';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { NotForThisAccount } from './Review';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';

/**
 * The review queue.
 *
 * Ordered by the API, not here: a critical value outranks priority, which
 * outranks an SLA breach.
 *
 * Doctors can toggle their availability status (Available / Away).
 * When Away, no new examinations will be auto-assigned to them.
 * Browser notifications alert the doctor when new reviews arrive.
 */
export function Queue() {
  const { permissions } = useAuth();
  // Mirrors the API's check, so another role reaching this URL is told so
  // instead of meeting a raw permission error.
  if (!holds(permissions, 'review.queue-read')) return <NotForThisAccount />;
  return <QueueScreen />;
}

function QueueScreen() {
  const nav = useNavigate();
  const [nonce, setNonce] = useState(0);
  const me = useQuery<PhysicianRow>(() => api.get('/physician/me'), [nonce]);
  const queue = useQuery<ReviewQueueRow[]>(() => api.get('/physician/queue?limit=100'), [nonce]);
  const [q, setQ] = useState('');
  // One of the strip's counts, as a filter. Counted from the queue as the API
  // ordered it; the order itself is never changed here.
  const [focus, setFocus] = useState<Focus>('all');
  const [togglingAvailability, setTogglingAvailability] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // Notification state
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(() => {
    return typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'default';
  });

  const previousCountRef = useRef<number | null>(null);

  // Auto-refresh every 30 seconds while tab is visible
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') setNonce((n) => n + 1);
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        new Notification('Notifications Enabled', {
          body: 'You will be notified when new examinations arrive for your review.',
          icon: '/favicon.ico',
        });
      }
    }
  };

  // Trigger browser notification when new items arrive
  useEffect(() => {
    if (!queue.data) return;
    const currentCount = queue.data.length;

    if (
      previousCountRef.current !== null &&
      currentCount > previousCountRef.current &&
      notificationPermission === 'granted'
    ) {
      const newCount = currentCount - previousCountRef.current;
      const latest = queue.data[0];
      new Notification(`${newCount} New Examination${newCount > 1 ? 's' : ''} Ready for Review`, {
        body: latest
          ? `${latest.employee.fullName} (${EXAM_TYPE_LABEL[latest.examinationType] ?? latest.examinationType}) is awaiting review.`
          : 'New examinations have been assigned to your review queue.',
        icon: '/favicon.ico',
      });
    }

    previousCountRef.current = currentCount;
  }, [queue.data, notificationPermission]);

  // Toggle doctor availability
  async function toggleAvailability() {
    if (!me.data) return;
    setTogglingAvailability(true);
    setToggleError(null);
    try {
      const next = !(me.data.isAvailable ?? true);
      await api.put('/physician/me/availability', { isAvailable: next });
      setNonce((n) => n + 1);
    } catch (e) {
      setToggleError(errorMessage(e));
    } finally {
      setTogglingAvailability(false);
    }
  }

  const isAvailable = me.data?.isAvailable ?? true;
  const all = queue.data ?? [];
  const needle = q.trim().toLowerCase();
  const counts: Record<Focus, number> = {
    all: all.length,
    critical: all.filter((r) => FOCUS.critical(r)).length,
    overdue: all.filter((r) => FOCUS.overdue(r)).length,
    rereview: all.filter((r) => FOCUS.rereview(r)).length,
  };
  const rows = all.filter(
    (r) =>
      FOCUS[focus](r) && (
        !needle ||
        r.employee.fullName.toLowerCase().includes(needle) ||
        r.employee.employeeCode.toLowerCase().includes(needle) ||
        !!r.company?.toLowerCase().includes(needle)),
  );
  const canNotify = typeof window !== 'undefined' && 'Notification' in window && notificationPermission === 'default';

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Review queue"
        hint={
          <>
            {queue.data ? <strong className="font-semibold text-neutral-700">{all.length} waiting. </strong> : null}
            Examinations assigned to you once every mandatory result is released. Re-reviews and critical values come first.
            Once signed, a review moves to <Link to="/physician/history" className="tap-area font-medium text-primary-600 hover:underline">Review history</Link>.
          </>
        }
        actions={
          <>
            {me.data && (
              /* A toggle, and it says so: the pressed state is the current
                 status, and the label never changes meaning on click. */
              <button
                type="button"
                onClick={toggleAvailability}
                disabled={togglingAvailability}
                aria-pressed={isAvailable}
                className={cn(
                  'inline-flex h-8 max-md:h-10 items-center gap-1.5 rounded-btn border px-3 text-caption font-semibold transition-colors duration-hover disabled:opacity-60',
                  isAvailable
                    ? 'border-success-600/30 bg-success-100 text-success-600 hover:bg-success-100/70'
                    : 'border-neutral-300 bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                )}
                title={
                  isAvailable
                    ? 'You receive new reviews. Select to mark yourself away (stops new auto-assignments).'
                    : 'You are away. Select to mark yourself available (resumes auto-assignments).'
                }
              >
                <span aria-hidden className={cn('h-2 w-2 rounded-full', isAvailable ? 'bg-success-600' : 'bg-neutral-500')} />
                {isAvailable ? 'Available for new reviews' : 'Away — not receiving reviews'}
              </button>
            )}
            <MoreActions>
              {canNotify && (
                <Button variant="secondary" size="sm" onClick={requestNotificationPermission}>
                  <Bell aria-hidden className="h-3.5 w-3.5" /> Enable notifications
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setNonce((n) => n + 1)} loading={queue.loading && !!queue.data}>
                <RefreshCw aria-hidden className="h-3.5 w-3.5" /> Refresh
              </Button>
            </MoreActions>
          </>
        }
      />

      {!isAvailable && (
        <Alert tone="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2">
              <PauseCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">You are marked as away.</strong>{' '}
                New examinations are not auto-assigned to you until you mark yourself available.
              </span>
            </div>
            <Button size="sm" onClick={toggleAvailability} loading={togglingAvailability} className="shrink-0">
              Mark available
            </Button>
          </div>
        </Alert>
      )}

      {toggleError && <Alert tone="danger">{toggleError}</Alert>}
      {me.error && <Alert tone="danger">{me.error}</Alert>}

      {queue.data && all.length > 0 && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          {/* What is waiting, by what makes it urgent. Each count filters the
              list below; the list keeps the API's priority order. */}
          <div role="group" aria-label="Filter the queue" className="flex flex-wrap gap-2 max-md:-mx-1 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-1 max-md:pb-1">
            {FOCUS_CHIPS.map(({ key, label, icon: Icon, tone }) => {
              const n = counts[key];
              const on = focus === key;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={on}
                  disabled={key !== 'all' && n === 0}
                  onClick={() => setFocus(on && key !== 'all' ? 'all' : key)}
                  className={cn(
                    'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-btn border px-3 text-small font-medium transition-colors duration-hover max-md:h-10',
                    'disabled:cursor-default disabled:opacity-50',
                    on ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
                  )}
                >
                  <Icon aria-hidden className={cn('h-4 w-4', n > 0 && tone)} />
                  {label}
                  <span className={cn('tabular-nums font-semibold', n > 0 && key !== 'all' ? tone : 'text-neutral-900')}>{n}</span>
                </button>
              );
            })}
          </div>
          {all.length > 4 && (
            <SearchInput
              value={q}
              onValueChange={setQ}
              placeholder="Employee name, code or company"
              aria-label="Search the queue"
              className="w-full sm:w-72"
            />
          )}
        </div>
      )}
      {all.length >= 100 && (
        <p className="text-caption text-neutral-500">Showing the first 100 in priority order; the counts above cover those 100.</p>
      )}

      <DataTable<ReviewQueueRow>
        rows={rows}
        // The queue refreshes itself every 30 seconds; only the first load
        // shows a skeleton, so the list does not blank while it is being read.
        loading={queue.loading && !queue.data}
        error={queue.error}
        onRetry={queue.reload}
        rowKey={(r) => r.orderId}
        onRowClick={(r) => nav(`/physician/review/${r.orderId}`)}
        empty={focus !== 'all' && !needle
          ? { title: 'Nothing in this group', hint: 'Select All waiting to see the whole queue.' }
          : needle
          ? { title: 'No employee matches that search', hint: 'Search by the employee’s name or employee code, or the company, or clear the search.' }
          : {
            title: 'Nothing is waiting for you',
            hint: isAvailable
              ? 'An examination is assigned here automatically once the venue and the laboratory have released every mandatory result.'
              : 'You are marked as away. Mark yourself available to start receiving new examinations.',
          }}
        columns={[
          {
            key: 'employee',
            header: 'Employee',
            cell: (r) => (
              <div>
                <div className="font-medium text-neutral-900">{r.employee.fullName}</div>
                <div className="text-caption font-normal text-neutral-500">
                  <span className="font-mono">{r.employee.employeeCode}</span>
                  {r.company ? ` · ${r.company}` : ''}
                </div>
              </div>
            ),
          },
          {
            key: 'type',
            header: 'Examination',
            cell: (r) => (
              <span className="text-small text-neutral-700">
                {EXAM_TYPE_LABEL[r.examinationType] ?? r.examinationType.replace(/_/g, ' ').toLowerCase()}
              </span>
            ),
          },
          {
            key: 'flags',
            header: 'Priority',
            cell: (r) => (
              <div className="flex flex-wrap gap-1">
                {r.reassessment && (
                  <StatusBadge tone="danger">
                    <RotateCcw aria-hidden className="h-3 w-3" />
                    Re-review · report amended (rev {r.reassessment.revision})
                  </StatusBadge>
                )}
                {r.correction && (
                  <StatusBadge tone="info">
                    <FilePenLine aria-hidden className="h-3 w-3" />
                    Correction approved · sign afresh
                  </StatusBadge>
                )}
                {/* A laboratory flag, not a fitness outcome: danger, never
                    the clinical "unfit" tone. */}
                {r.criticalFlags > 0 && (
                  <StatusBadge tone="danger">
                    <Siren aria-hidden className="h-3 w-3" />
                    {r.criticalFlags} critical value{r.criticalFlags === 1 ? '' : 's'}
                  </StatusBadge>
                )}
                {r.slaBreached && <StatusBadge tone="overdue">Past review deadline</StatusBadge>}
                {r.priority !== 'ROUTINE' && (
                  <StatusBadge tone="warning">{PRIORITY_LABEL[r.priority] ?? capitalise(r.priority)}</StatusBadge>
                )}
                {r.criticalFlags === 0 && !r.slaBreached && r.priority === 'ROUTINE' && !r.reassessment && !r.correction && (
                  <span className="text-small text-neutral-500">Routine</span>
                )}
              </div>
            ),
          },
          {
            key: 'waiting',
            header: 'Waiting / due',
            cell: (r) => (
              <div className="text-small tabular-nums">
                <div className="text-neutral-700">{formatDateTime(r.waitingSince)}</div>
                {r.dueDate && (
                  <div className={cn('text-caption', r.slaBreached ? 'font-medium text-overdue-600' : 'text-neutral-500')}>
                    {r.slaBreached ? 'Was due' : 'Due'} {formatDate(r.dueDate)}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'go',
            header: 'Actions',
            align: 'right',
            alwaysVisible: true,
            mobileLabel: '',
            cell: (r) => (
              <Button
                size="sm"
                icon={<ClipboardCheck aria-hidden className="h-4 w-4" />}
                aria-label={`${r.reassessment || r.correction ? 'Re-review' : 'Review'} ${r.employee.fullName}`}
                onClick={(e) => { e.stopPropagation(); nav(`/physician/review/${r.orderId}`); }}
              >
                {r.reassessment || r.correction ? 'Re-review' : 'Review'}
              </Button>
            ),
          },
        ]}
      />

      <Governance />
    </div>
  );
}

/**
 * The company baseline template: clinical governance, not the day's reviews.
 * Kept on this console (it is the physician's to approve) but folded under the
 * queue, so the first thing on the page is what is waiting.
 */
function Governance() {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left max-md:min-h-12"
      >
        <span>
          <span className="block text-small font-semibold text-neutral-900">Standard occupational health checkup template</span>
          <span className="block text-caption text-neutral-500">Clinical governance: approve the company baseline tests</span>
        </span>
        <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 text-neutral-500 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="border-t border-neutral-200 p-4"><OccupationalBaselines /></div>}
    </Card>
  );
}

type Focus = 'all' | 'critical' | 'overdue' | 'rereview';
const FOCUS: Record<Focus, (r: ReviewQueueRow) => boolean> = {
  all: () => true,
  critical: (r) => r.criticalFlags > 0,
  overdue: (r) => r.slaBreached,
  rereview: (r) => !!(r.reassessment || r.correction),
};
const FOCUS_CHIPS: Array<{ key: Focus; label: string; icon: typeof Inbox; tone: string }> = [
  { key: 'all', label: 'All waiting', icon: Inbox, tone: 'text-neutral-900' },
  { key: 'critical', label: 'Critical values', icon: Siren, tone: 'text-danger-600' },
  { key: 'overdue', label: 'Past deadline', icon: Clock, tone: 'text-overdue-600' },
  { key: 'rereview', label: 'Re-reviews', icon: RotateCcw, tone: 'text-primary-700' },
];

const PRIORITY_LABEL: Record<string, string> = { HIGH: 'High', URGENT: 'Urgent' };

function capitalise(s: string): string {
  const lower = s.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
